import { NetworkFile } from "./NetworkTools.js";
import { TimeConversion, EventMethods } from "./HelperTools.js"
export { WebVTTThumbnailUtils, WebVTTThumbnail }

/**
 * Class that handles webvtt files
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 */
class WebVTT {
    /**
     * Build a webVTT object, ready to be filled with webVTT related data
     * @param {string} webVTTPath - path to the webVTT file
     */
    constructor(webVTTPath) {
        this.webVTTPath = webVTTPath;
        this.manifestSource = [];
    }

    /**
     * Load the webVTT file and include the manifest into the object
     * @param {callback} - function to be executed subsequently
     * @returns {Promise} Promise that is resolved when manifest file was read in successfully
     */
    readFile() {
        return new Promise((resolve, reject) => {
            NetworkFile.getHttpFile(this.webVTTPath, 'text/vtt', data => {

                // \n\r\n when webvtt file has been written with windows, \n\n with linux 
                this.manifestSource = data.split(/\n\r\n|\n\n/).map(function (item) {
                    var parts = item.split('\n')
                    return {
                        time_start: parts[0].split(' --> ')[0],
                        time_end: parts[0].split(' --> ')[1],
                        data: parts[1],
                    };
                })
                if (typeof this.manifestSource !== 'undefined')
                    return resolve();
                else
                    return reject();
            });
        })
    }
}

/**
 * Class that handles webVTT files for thumbnails 
 * @memberof vef.ui.media
 * @extends WebVTT
 * @author ckraemme <ckraemme@awi.de>
 */
class WebVTTThumbnail extends WebVTT {
    /**
     * Build a webVTT object for thumbnails, ready to be filled with thumbnail related data
     * @param {string} webVTTPath - path to the webVTT file containing thumbnail information
     */
    constructor(webVTTPath) {
        super(webVTTPath);
        this.manifestData = [];
        this.thumbnailLoadingState = [];
    }

    /**
     * Read in the thumbnail manifest file (webVTT) and store its information in a machine readable form. Afterwards preload thumbnails.
     * @param {callback} - function to be executed subsequently
     * @param {Number} num - number of thumbnails that should be preloaded. If num=-1, load all.
     * @param {boolean} sparse - if true, load the thumbnails spread over the whole video, otherwise sequentially from the beginning (for separated thumbnail images only) 
     * @returns {Promise} Promise that is resolved after thumbnails were loaded successfully
     */
    init(num, sparse) {
        return this.readFile().then(() => {
            this.reduceManifestViaTimestamps_();
            this.extractUrlsInManifest_();
            return this.preloadThumbnails_(num, sparse)
        })
    }

    /**
     * Remove all lines in the manifest file that do not contain timestamps and data
     * @private
     */
    reduceManifestViaTimestamps_() {
        this.manifestSource.forEach(line => {
            if (line['time_start'] != null && line['time_end'] != null && line['data'] != null)
                this.manifestData.push(line);
        })
    }

    /**
     * For each thumbnail image, extract the pure thumbnail url, i.e., removing trailing coordinate information if available, and create the absolute url path
     * @private
     */
    extractUrlsInManifest_() {
        var urlWebVTTPath = new URL(this.webVTTPath);

        for (let i = 0; i < this.manifestData.length; i++) {
            const urlWebVTTImage = this.manifestData[i]['data'].split('#')[0];

            var regexAbsoluteUrl = new RegExp('^(?:[a-z+]+:)?//', 'i');
            var regexRootUrl = new RegExp('^/(?!/)', 'i');

            const isAbsoluteUrl = regexAbsoluteUrl.test(urlWebVTTImage);
            const isRootUrl = regexRootUrl.test(urlWebVTTImage);
            const isRelativeUrl = !isAbsoluteUrl && !isRootUrl;

            var base;
            if (isAbsoluteUrl)
                base = undefined;
            else if (isRootUrl)
                base = urlWebVTTPath.protocol + '//' + urlWebVTTPath.hostname;
            else if (isRelativeUrl)
                base = this.webVTTPath.substring(0, this.webVTTPath.lastIndexOf("/")) + '/';

            const urlObject = new URL(urlWebVTTImage, base);
            this.manifestData[i]['url'] = urlObject['href'];
        }
    }

    /**
     * Preload thumbnail images and write the image data into this.thumbnailLoadingState
     * @param {Number} num - number of thumbnails that should be preloaded. If num=-1, load all.
     * @param {boolean} sparse - if true, load the thumbnails spread over the whole video, otherwise sequentially from the beginning (for separated thumbnail images only) 
     * @private
     */
    preloadThumbnails_(num, sparse) {

        if (num == -1)
            num = this.manifestData.length;

        let delta = 1;
        if (sparse == true) {
            delta = Math.floor(this.manifestData.length / num);
            delta = Math.max(delta, 1) //in case of wrong configuration
        }

        for (let i = 0; i < this.manifestData.length; i++)
            this.manifestData[i]['image'] = undefined;

        for (let i = 0; i < this.manifestData.length; i = i + delta) {
            this.loadThumbnail_(i);
            if (i == (num - 1) * delta)
                break;
        }
        return Promise.all(this.thumbnailLoadingState)
    }

    /**
     * Load the thumbnail image
     * @param {Number} pos - position of the thumbnail in this.manifestData
     * @private
     */
    loadThumbnail_(pos) {
        if (this.thumbnailLoadingState[pos] === undefined) {
            this.thumbnailLoadingState[pos] = new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => {
                    this.manifestData[pos]['image'] = image;
                    resolve(image);
                }
                image.onerror = reject;
                image.src = this.manifestData[pos]['url'];
            })
        }
    }
}

/**
 * Class that provides tools for thumbnail manifests
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class WebVTTThumbnailUtils {

    /**
     * Based on the pointer position of an event that is triggered for an HTML element, its respective position of the thumbnail is calculated. The beginning of the element corresponds to the first thumbnail and the end to the last.
     * @param {Event} event - HTML event object containing the event target
     * @param {Object} manifestData - manifest data in a machine readable format from WebVTTThumbnail.manifestData
     * @param {boolean} isSeekbar - indicates if the event is triggered by a seek bar, i.e., when the pointer hovers above an HTML 'input' element where the 'type' attribute is 'range'
     * @returns {Array} Return the manifest data information for the thumbnail object at pointer position and its timestamp
     */
    static getManifestObjectOnHover(event, manifestData, isSeekbar = false) {

        if (isSeekbar)
            var [relativePositionX,] = EventMethods.getPointerPositionRelativeToSeekbar(event);
        else
            var [relativePositionX,] = EventMethods.getPointerPositionRelativeToEventTarget(event);

        const maxFrame = manifestData.length;
        const startTimeMS = TimeConversion.isoDayTime2Milliseconds(manifestData[0]['time_start']);
        const endTimeMS = TimeConversion.isoDayTime2Milliseconds(manifestData[maxFrame - 1]['time_end']);

        const currentTimeMS = relativePositionX * (endTimeMS - startTimeMS);
        const currentTimeHHMMSSms = TimeConversion.milliseconds2IsoDayTime(currentTimeMS);
        const manifestDataAtTimestamp = WebVTTThumbnailUtils.getManifestObjectViaTimestamp(currentTimeHHMMSSms, manifestData);
        return [manifestDataAtTimestamp, currentTimeHHMMSSms]
    }

    /**
     * Get all the information inside a manifest data object for a specific timestamp.
     * @param {String} currentTimeHHMMSSms - timestamp in hh:mm:ss.ms format 
     * @param {Object} manifestData - manifest data in a machine readable format from WebVTTThumbnail.manifestData
     * @returns {Array} Return the manifest data information for the thumbnail object at the timestamp
     */
    static getManifestObjectViaTimestamp(currentTimeHHMMSSms, manifestData) {

        const maxFrame = manifestData.length;
        for (let i = 0; i < maxFrame; i++) {
            if ((manifestData[i]['time_start'] <= currentTimeHHMMSSms) && (manifestData[i]['time_end'] > currentTimeHHMMSSms)) {
                return manifestData[i]
            }
        }
        if (manifestData[manifestData.length - 1]['time_end'] == currentTimeHHMMSSms)
            return manifestData[manifestData.length - 1]

        if (manifestData[0]['time_start'] > currentTimeHHMMSSms) {
            console.warn(`Requested thumbnail for timestamp ${currentTimeHHMMSSms} is out of range. Start of video in manifest: ${manifestData[0]['time_start']}.`)
            return manifestData[0]
        }

        if (manifestData[manifestData.length - 1]['time_end'] < currentTimeHHMMSSms) {
            console.warn(`Requested thumbnail for timestamp ${currentTimeHHMMSSms} is out of range. End of video in manifest: ${manifestData[manifestData.length - 1]['time_end']}.`)
            return manifestData[manifestData.length - 1]
        }
        return undefined
    }

    /**
     * Reduce the information inside the thumbnail manifest by deleting timestamps and data information. Time correction for the remaining thumbnail images is applied. I.e., this method is useful for making the thumbnail preview in a seek bar in a video player less sensitive to mouse movement
     * @param {Object} manifestData - manifest data in a machine readable format from WebVTTThumbnail.manifestData
     * @param {Number} number - number of thumbnails that should be preserved
     * @returns {Object} reduced manifest data
     */
    static reduceManifestEvenlyDistributed(manifestData, number) {
        const maxFrame = manifestData.length;
        if (number >= maxFrame) {
            return manifestData;
        }

        const result = [];
        const interval = maxFrame / number;

        for (let i = 0; i < number; i++) {
            const evenIndex = Math.floor(i * interval);
            result.push(manifestData[evenIndex]);
        }

        for (let i = 0; i < result.length; i++) {
            if (i == result.length - 1)
                result[i]['time_end'] = manifestData[maxFrame - 1]['time_end'];
            else
                result[i]['time_end'] = result[i + 1]['time_start'];
        }
        return result;
    }
}